import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Alert,
  InputAdornment,
  Typography,
  Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import {
  Favorite as FavoriteIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

function SectionHeader({ icon, label }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '8px',
        bgcolor: alpha(theme.palette.secondary.main, 0.2),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 15, color: 'primary.dark' } })}
      </Box>
      <Typography variant="overline" sx={{ color: 'primary.dark', fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
    </Box>
  );
}

const EMPTY_FORM = {
  name: '',
  eventDate: null,
  eventTime: null,
  estimatedGuests: '',
  totalBudget: '',
  venueName: '',
  venueAddress: '',
  venueCity: '',
  venueZipCode: '',
  categories: [],
};

function CreateWeddingEventDialog({ open, onClose, onSubmit }) {
  const theme = useTheme();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) fetchCategories();
  }, [open]);

  const fetchCategories = async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.CATEGORIES.LIST);
      if (response.success) {
        setAvailableCategories(response.data.filter((cat) => cat.isActive));
      }
    } catch (err) {
      console.error('Fetch categories error:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.find((c) => c.categoryId === categoryId)
        ? prev.categories.filter((c) => c.categoryId !== categoryId)
        : [...prev.categories, { categoryId, isRequired: true }],
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { setError('Az esemény neve kötelező'); return; }
    if (!formData.eventDate) { setError('Az esemény dátuma kötelező'); return; }

    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        name: formData.name.trim(),
        eventDate: formData.eventDate.toISOString(),
        eventTime: formData.eventTime
          ? `${String(formData.eventTime.getHours()).padStart(2, '0')}:${String(formData.eventTime.getMinutes()).padStart(2, '0')}`
          : null,
        estimatedGuests: formData.estimatedGuests ? parseInt(formData.estimatedGuests) : null,
        totalBudget: formData.totalBudget ? parseFloat(formData.totalBudget) : null,
        venueName: formData.venueName.trim() || null,
        venueAddress: formData.venueAddress.trim() || null,
        venueCity: formData.venueCity.trim() || null,
        venueZipCode: formData.venueZipCode.trim() || null,
        categories: formData.categories.map((cat) => ({
          categoryId: cat.categoryId,
          isRequired: cat.isRequired,
        })),
      });
      setFormData(EMPTY_FORM);
    } catch (err) {
      setError(err.message || 'Hiba történt az esemény létrehozása során');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(EMPTY_FORM);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Fejléc */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            bgcolor: alpha('#fff', 0.15),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FavoriteIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', lineHeight: 1.2 }}>
              Új esküvő esemény
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
              Töltsd ki az alapadatokat a kezdéshez
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Alapadatok */}
        <SectionHeader icon={<EventIcon />} label="Alapadatok" />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Esemény neve"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="pl. Anna és Péter Esküvője"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
              <DatePicker
                label="Esemény dátuma"
                value={formData.eventDate}
                onChange={(v) => handleChange('eventDate', v)}
                minDate={new Date()}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
              <TimePicker
                label="Esemény időpontja"
                value={formData.eventTime}
                onChange={(v) => handleChange('eventTime', v)}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Becsült vendégszám"
              type="number"
              fullWidth
              value={formData.estimatedGuests}
              onChange={(e) => handleChange('estimatedGuests', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Teljes költségvetés"
              type="number"
              fullWidth
              value={formData.totalBudget}
              onChange={(e) => handleChange('totalBudget', e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">Ft</InputAdornment>,
                inputProps: { min: 0, step: 10000 },
              }}
            />
          </Grid>
        </Grid>

        {/* Helyszín */}
        <SectionHeader icon={<LocationIcon />} label="Helyszín" />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Helyszín neve"
              fullWidth
              value={formData.venueName}
              onChange={(e) => handleChange('venueName', e.target.value)}
              placeholder="pl. Grand Hotel Budapest"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Utca, házszám"
              fullWidth
              value={formData.venueAddress}
              onChange={(e) => handleChange('venueAddress', e.target.value)}
              placeholder="pl. Fő utca 1."
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              label="Város"
              fullWidth
              value={formData.venueCity}
              onChange={(e) => handleChange('venueCity', e.target.value)}
              placeholder="pl. Budapest"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Irányítószám"
              fullWidth
              value={formData.venueZipCode}
              onChange={(e) => handleChange('venueZipCode', e.target.value)}
              placeholder="1011"
              inputProps={{ maxLength: 4 }}
            />
          </Grid>
        </Grid>

        {/* Kategóriák */}
        <SectionHeader icon={<CategoryIcon />} label="Szükséges szolgáltatók" />
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableCategories.map((category) => {
              const selected = !!formData.categories.find((c) => c.categoryId === category.id);
              return (
                <Chip
                  key={category.id}
                  label={category.name}
                  icon={category.icon ? <span style={{ fontSize: 14 }}>{category.icon}</span> : undefined}
                  onClick={() => handleCategoryToggle(category.id)}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  sx={{ fontWeight: selected ? 600 : 400 }}
                />
              );
            })}
          </Box>
          {formData.categories.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Válaszd ki azokat a kategóriákat, amelyekre szükséged lesz. Később is módosítható.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: 'text.secondary' }}>
          Mégse
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ px: 3 }}
        >
          {loading ? 'Létrehozás...' : 'Létrehozás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateWeddingEventDialog;
